import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, distinctUntilChanged, map, merge, Observable, of, shareReplay, startWith, switchMap, timer } from 'rxjs';
import { AiChatInternalPromptService } from '@panaya/ai-chat/domain';
import { injectAiChatEventNotifier } from '../ai-chat-events.provider';
import { ThinkingEvent } from '@panaya/ai-chat/shared';

const ONE_MOMENT_EVENT: ThinkingEvent = { state: 'thinking', translationKey: 'aichat.thinking.one_moment' };
const THINKING_EVENT: ThinkingEvent = { state: 'thinking', translationKey: 'aichat.thinking.in_progress' };
const TIMEOUT_MS = 5000;

@Injectable({ providedIn: 'root' })
export class AiChatThinkingService {
    private readonly eventSubject = new BehaviorSubject<ThinkingEvent>(ONE_MOMENT_EVENT);

    public readonly thinking$: Observable<ThinkingEvent> = this.eventSubject.pipe(
        switchMap((event) => {
            if (event.state === 'completed') {
                return timer(TIMEOUT_MS).pipe(
                    map(() => THINKING_EVENT),
                    startWith(event)
                );
            }
            return of(event);
        }),
        distinctUntilChanged(isSameThinkingEvent),
        shareReplay(1)
    );

    constructor() {
        const uiEvents = injectAiChatEventNotifier();
        const internalPromptService = inject(AiChatInternalPromptService);
        merge(uiEvents.promptRequest, internalPromptService.internalPrompt$)
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.reset());
    }

    public notifyThinkingEvent(event: ThinkingEvent): void {
        this.eventSubject.next(event);
    }

    private reset(): void {
        this.eventSubject.next(ONE_MOMENT_EVENT);
    }
}

function isSameThinkingEvent(a: ThinkingEvent, b: ThinkingEvent): boolean {
    return (
        a.state === b.state &&
        a.translationKey === b.translationKey &&
        JSON.stringify(a.translationParams) === JSON.stringify(b.translationParams)
    );
}
